<template>
  <div class="photos-manager">
    <draggable
      :value="localPhotos"
      class="photos-grid"
      :disabled="readOnly"
      @input="handleReorder"
      @choose="handleDragChoose"
      @start="dragStarted = true"
      @unchoose="handleDragUnchoose"
      @end="handleDragEnd"
    >
      <div
        v-for="(photo, index) in localPhotos"
        :key="photo.id"
        class="photo-item"
        :class="{ deleted: photo.deleted }"
      >
        <img
          :src="photo.url"
          :alt="`Photo ${index + 1}`"
          :style="{ opacity: photo.deleted ? 0.5 : 1 }"
           @error="handleImageError(index)"
        />
        <div class="photo-actions">
          <button
            class="btn-icon"
            @click="toggleDelete(index)"
            :disabled="readOnly"
            :class="{ 'is-delete': photo.deleted }"
          >
            <SvgIcon :name="photo.deleted ? 'eye' : 'hide'" />
          </button>
        </div>
        <div class="photo-position">{{ index + 1 }}</div>
      </div>
    </draggable>
  </div>
</template>

<script>
import draggable from "vuedraggable";
import SvgIcon from "@/components/ui/svg/SvgIcon.vue";

export default {
  name: "PhotosManager",

  components: {
    draggable,
    SvgIcon,
  },

  props: {
    photos: {
      type: Array,
      required: true,
    },
    articleId: {
      type: String,
      required: true,
    },
    // Consultation seule : l'utilisateur n'a pas le droit d'enregistrer,
    // on lui interdit donc aussi de réorganiser ou masquer les photos.
    readOnly: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      localPhotos: [],
      // Photos masquées côté CMS (position -1) : jamais affichées ni
      // réordonnées, mais conservées pour être renvoyées telles quelles
      // au back lors d'un update (sinon leur masquage serait perdu).
      hiddenPhotos: [],
      dragHeld: false,
      dragStarted: false,
      pendingPhotos: null,
    };
  },

  watch: {
    photos: {
      immediate: true,
      handler(newPhotos) {
        if (this.dragHeld) {
          this.pendingPhotos = newPhotos;
          return;
        }
        this.applyPhotos(newPhotos);
      },
    },
  },

  methods: {
    applyPhotos(newPhotos) {
      const mapped = newPhotos.map((photo) => ({
        ...photo,
        url: photo.url,
        id: photo.id,
        position: photo.position,
        deletedAt: photo.deletedAt,
        deleted: !!photo.deletedAt,
      }));

      this.localPhotos = mapped.filter((photo) => photo.position !== -1);
      this.hiddenPhotos = mapped.filter((photo) => photo.position === -1);
    },

    handleDragChoose() {
      this.dragHeld = true;
      this.dragStarted = false;
    },

    handleDragUnchoose() {
      if (!this.dragStarted) {
        this.releaseDrag();
      }
    },

    releaseDrag() {
      this.dragHeld = false;
      this.dragStarted = false;
      const pending = this.pendingPhotos;
      this.pendingPhotos = null;
      if (pending) {
        this.applyPhotos(pending);
      }
      return !!pending;
    },

    handleReorder(photos) {
      this.localPhotos = photos.filter(Boolean);
    },

    handleDragEnd() {
      if (this.releaseDrag() || this.readOnly) return;

      this.updatePhotos();
    },

    setPrimary(index) {
      if (this.localPhotos[index].deleted) return;

      this.localPhotos = this.localPhotos.map((photo, i) => ({
        ...photo,
        isPrimary: i === index,
      }));
      this.updatePhotos();
    },

    toggleDelete(index) {
      if (this.readOnly) return;

      const photo = this.localPhotos[index];

      photo.deleted = !photo.deleted;
      // Garder deletedAt cohérent avec deleted : le watch de la prop `photos`
      // dérive `deleted` depuis `!!deletedAt`. Sans cette synchro, une photo
      // fraîchement masquée (deletedAt encore null) est redérivée à deleted=false
      // dès qu'un SET_ARTICLE re-déclenche le watch (ex. après un signalement).
      photo.deletedAt = photo.deleted ? new Date().toISOString() : null;

      const nonDeleted = [];
      const deleted = [];

      this.localPhotos.forEach((p) => {
        if (!p.deleted) {
          nonDeleted.push(p);
        } else {
          deleted.push(p);
        }
      });

      this.localPhotos = [...nonDeleted, ...deleted];
      
      this.updatePhotos();
    },

    // Les réorganisations et masquages ne sont jamais envoyés au back depuis
    // ce composant : on remonte l'état courant au parent, qui l'enverra au
    // moment de l'enregistrement, dans le même PUT que le metadata.
    updatePhotos() {
      this.$emit("update:photos", [
        ...this.localPhotos.map((photo, index) => ({
          ...photo,
          position: index,
        })),
        ...this.hiddenPhotos.map((photo) => ({ ...photo, position: -1 })),
      ]);
    },
    handleImageError(index) {
      if (!this.localPhotos[index].deleted) {
        console.warn(`Image failed to load at index ${index}:`, this.localPhotos[index].url);
      }
    },
  },
};
</script>

<style scoped>
.photos-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 12px;
  margin: 20px 0;
}

.photo-item:first-child {
  grid-column: span 2;
  grid-row: span 2;
}

.photo-item {
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  background: white;
  aspect-ratio: 2/3;
  cursor: grab;

  &:after {
    content: "";
    display: block;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    z-index: 5;
    mix-blend-mode: multiply;
    position: absolute;
    background: #fff;
    background: radial-gradient(circle, #fff, #f5f3f1);
    pointer-events: none;
  }
}

.photo-item.deleted::before {
  content: "";
  position: absolute;
  top: 50%;
  left: -42%;
  width: 188%;
  height: 1px;
  background: #e1e1e1;
  transform: rotate(56deg);
  z-index: 10;
  pointer-events: none;
}

.photo-item img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
  transition: opacity 0.3s ease;
}

.photo-actions {
  position: absolute;
  top: 12px;
  right: 12px;
  display: flex;
  gap: 8px;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.photo-item:hover .photo-actions {
  opacity: 1;
}

.btn-icon {
  width: 32px;
  height: 32px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.95);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.btn-icon:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.photo-position {
  position: absolute;
  bottom: 12px;
  left: 12px;
  background: rgba(0, 0, 0, 0.6);
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
}

.photos-grid[aria-disabled="true"] {
  opacity: 0.7;
  pointer-events: none;
}

@media (max-width: 1024px) {
  .photos-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 768px) {
  .photos-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}
</style>
